/** Track05 local UX values only; never an Owner contract or cross-portal DTO. */
export interface DemoAssetFields {
  readonly name: string;
  readonly category: string;
  readonly temporaryNo: string;
  readonly configuration: string;
  readonly source: string;
}
export type DemoAssetStatus = 'DRAFT' | 'PENDING_REVIEW' | 'CHANGES_REQUESTED' | 'PUBLISHED';
export interface DemoAssetDraft {
  readonly demoAssetDraftId: string;
  readonly fields: DemoAssetFields;
  readonly status: DemoAssetStatus;
  readonly version: number;
  readonly origin: 'STATIC_DEMO' | 'SESSION_ONLY';
  readonly publishedV1: DemoAssetFields | null;
  readonly feedback: string;
}
export const demoAssetLabels: Readonly<Record<DemoAssetStatus, string>> = {
  DRAFT: '草稿（演示）',
  PENDING_REVIEW: '待审核（演示）',
  CHANGES_REQUESTED: '退回修改（演示）',
  PUBLISHED: '已发布（演示样例）',
};
export const demoAssetFieldLabels: Readonly<Record<keyof DemoAssetFields, string>> = {
  name: '装备名称',
  category: '设备类别',
  temporaryNo: '演示临时编号（可选）',
  configuration: '具体配置与用途声明（未经验证）',
  source: '资料来源与备注',
};
export const emptyDemoAsset: DemoAssetFields = Object.freeze({
  name: '',
  category: '',
  temporaryNo: '',
  configuration: '',
  source: '',
});
const publishedV1: DemoAssetFields = Object.freeze({
  name: '消防侦察装备（独立演示）',
  category: '侦察装备',
  temporaryNo: 'DEMO-019',
  configuration: 'v1：机构用途声明，配置尚未核验。',
  source: '05 本端固定培训样例，非真实资产。',
});
export const demoAssetFixtures: readonly DemoAssetDraft[] = Object.freeze([
  Object.freeze({
    demoAssetDraftId: 'demoAssetDraftId-returned-01',
    fields: Object.freeze({
      ...publishedV1,
      temporaryNo: 'DEMO-020',
      configuration: 'v2：补充传感配置用途声明，尚未核验。',
    }),
    status: 'CHANGES_REQUESTED',
    version: 2,
    origin: 'STATIC_DEMO',
    publishedV1,
    feedback: '独立演示退回意见：请补充配置来源和使用限制。未收到真实08反馈。',
  }),
  Object.freeze({
    demoAssetDraftId: 'demoAssetDraftId-published-01',
    fields: publishedV1,
    status: 'PUBLISHED',
    version: 1,
    origin: 'STATIC_DEMO',
    publishedV1,
    feedback: '固定已发布演示 v1，只读；不进入 CA03 Owner 目录。',
  }),
]);
export function validateDemoAsset(
  fields: DemoAssetFields,
  rows: readonly DemoAssetDraft[],
  excludingId: string | null = null,
): Partial<Record<keyof DemoAssetFields, string>> {
  const errors: Partial<Record<keyof DemoAssetFields, string>> = {};
  const limits = { name: 80, category: 40, temporaryNo: 40, configuration: 1200, source: 600 };
  for (const key of Object.keys(limits) as (keyof DemoAssetFields)[]) {
    const value = fields[key];
    if (key !== 'temporaryNo' && value.trim() === '') errors[key] = '请填写此项，不能只有空格。';
    else if (value.length > limits[key]) errors[key] = `最多 ${limits[key]} 个字符。`;
    else if (
      /[<>]|javascript\s*:|data\s*:\s*text\/html/i.test(value) ||
      [...value].some((character) => {
        const code = character.charCodeAt(0);
        return code < 9 || code === 11 || code === 12 || (code > 13 && code < 32);
      })
    )
      errors[key] = '请使用普通文本，不能包含 HTML、脚本或危险地址。';
  }
  const number = fields.temporaryNo.trim().toLocaleLowerCase();
  if (
    number &&
    rows.some(
      (row) =>
        row.demoAssetDraftId !== excludingId &&
        row.fields.temporaryNo.trim().toLocaleLowerCase() === number,
    )
  )
    errors.temporaryNo = '此演示临时编号已存在，请使用另一个编号。';
  return errors;
}
export function saveDemoAsset(
  rows: readonly DemoAssetDraft[],
  fields: DemoAssetFields,
  id: string,
  editingId: string | null,
): readonly DemoAssetDraft[] {
  if (Object.keys(validateDemoAsset(fields, rows, editingId)).length) return rows;
  const previous = rows.find((row) => row.demoAssetDraftId === editingId);
  if (previous && previous.status !== 'DRAFT' && previous.status !== 'CHANGES_REQUESTED')
    return rows;
  const draft: DemoAssetDraft = {
    demoAssetDraftId: previous?.demoAssetDraftId ?? id,
    fields: { ...fields },
    status: 'DRAFT',
    version: previous?.version ?? 1,
    origin: 'SESSION_ONLY',
    publishedV1: previous?.publishedV1 ?? null,
    feedback: previous?.feedback ?? '',
  };
  return previous ? rows.map((row) => (row === previous ? draft : row)) : [...rows, draft];
}
export function submitDemoAsset(
  rows: readonly DemoAssetDraft[],
  id: string,
): readonly DemoAssetDraft[] {
  return rows.map((row) =>
    row.demoAssetDraftId === id &&
    row.status === 'DRAFT' &&
    !Object.keys(validateDemoAsset(row.fields, rows, id)).length
      ? { ...row, status: 'PENDING_REVIEW' }
      : row,
  );
}
export function proposeDemoAssetVersion(
  rows: readonly DemoAssetDraft[],
  id: string,
): readonly DemoAssetDraft[] {
  const published = rows.find((row) => row.demoAssetDraftId === id && row.status === 'PUBLISHED');
  const proposedId = `${id}-proposed-v2`;
  if (!published || rows.some((row) => row.demoAssetDraftId === proposedId)) return rows;
  return [
    ...rows,
    {
      ...published,
      demoAssetDraftId: proposedId,
      fields: { ...published.fields, temporaryNo: '' },
      publishedV1: published.fields,
      status: 'DRAFT',
      version: 2,
      origin: 'SESSION_ONLY',
      feedback: '拟议 v2 独立草稿，不覆盖已发布演示 v1。',
    },
  ];
}
